import classNames from "classnames"
import { FC, useEffect, useState } from "react"
import { DocumentType, IdTableVariant, LoadingStatus } from "../../../@types/enumsGlobal"
import { DefaultGrid, maxNumberPerPage } from "../../../components/grids/default/defaultGrid"
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins"
import { ICreateDocumentsRequest, ISalesReportViewDTO } from "../../../libs/coreapi-dto/accounting/lot"
import { LotDataProvider } from "../../../Services/DataProviders/LotDataProvider"
import useGridFilter, { DefaultGridFilter, DefaultLotGridFilter } from "../../../system/hooks/useGridFilter"
import { useAppContext } from "../../../system/providers/appContextProvider"
import { usePluginContext } from "../../../system/providers/plugin"
import styles from '../../styles/index.module.scss'
import DefaultLotsByCommandsPanel from "../../../components/commandsPanels/DefaultLotsByCommandsPanel"
import { GoodsDataProvider } from "../../../Services/DataProviders/GoodsDataProvider"
import { GoodsCreatorModal } from "../../Dictionaries/Goods"
import { useTabsContext } from "../../../system/providers/tabsProvider"
import { Spinner } from "../../../components/spiner/Spinner"
import { getFiltersForPrint } from "../../../system/functions/getFiltersForPrint"
import LotsByLotsCreatorModal from "../LotsByLots/LotsByLotsCreatorModal"
import { AnalyticalFilters, defaultAnalysisFilters, IAnalysisFilters, TypeAccordionName } from "./AnalyticalFilters"
import { Button } from "../../../components/buttons/button"
import FlexRow from "../../../components/controls/FlexRow"
import stylesAnalysis from "../AnalysisLot/Styles/AnalysisFilterLot.module.scss"
import Checkbox from "../../../components/checkboxes/checkbox"
import { useTranslation } from "react-i18next"
import { MessageModalWindow } from "../../../components/modalWindows/MessageModalWindow"
import { exportCsv } from "../../../system/functions/exportCsv"
import { DateTime } from "luxon"

export type typeSearchField = 'goodsCode' | 'goods' | 'internalBarcode' | 'externalBarcode'

interface IMessageModalProps {
    show: boolean,
    message: string,
    handler?: () => void
}

const AnalysisSalesLotPluginView: FC<IGridProps> = (props) => {
    const appContext = useAppContext();
    const tabCtx = useTabsContext();
    const lotDataProvider = new LotDataProvider(appContext.coreApiService);
    const goodsDataProvider = new GoodsDataProvider(appContext.coreApiService);

    const { t } = useTranslation();
    const baseT = (value: string) => t('analysisLot.' + value);

    const pluginCtx = usePluginContext();

    const [data, setData] = useState<ISalesReportViewDTO[]>([]);
    const [loadingStatus, setLoadingStatus] = useState<LoadingStatus>(LoadingStatus.NoData);
    const [totalCount, setTotalCount] = useState(0);
    const [allIds, setAllIds] = useState<string[]>();
    const [viewState, setViewState] = useState<GridStateType>("view");

    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultGridFilter);
    const [correctGridFilter, setCorrectGridFilter] = useState<IGridFilter>();
    const [analysisFilters, setAnalysisFilters] = useState<IAnalysisFilters>(defaultAnalysisFilters);

    const [selectedItem, setSelectedItem] = useState<IGridRow>();
    const [selectedItems, setSelectedItems] = useState<IGridRow[]>([]);
    const [selectedItemsInverion, setSelectedItemsInverion] = useState<IGridRow[]>([]);
    const [selectedIdGoods, setSelectedIdGoods] = useState<string | undefined>();
    const [selectedIdLot, setSelectedIdLot] = useState<string | undefined>();
    const [isAllSelect, setIsAllSelect] = useState<boolean>(false);
    
    const [multipleSelect] = useState<boolean>(true);

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [pluginSettings, setPluginSettings] = useState<IPluginSettings>(props.plugin);
    const [showMessageModal, setShowMessageModal] = useState<IMessageModalProps>({ show: false, message: '' });

    const isAllowCreateDocument = !(selectedItems.length > 0);

    const [loadDataTimeout, setLoadDataTimeout] = useState<NodeJS.Timeout>();

    const onChangeMultipleSelect = (rows: ISelectGridRow[], currentRow?: IGridRow): void => {
        if (rows) {
            if (isAllSelect && currentRow) {
                setSelectedItemsInverion?.(prev => {
                    const isExisting = prev.some(item => item.idGlobal === currentRow.idGlobal);
                    if (isExisting) {
                        return prev.filter(item => item.idGlobal !== currentRow.idGlobal);
                    } else {
                        return [...prev, currentRow];
                    }
                });
            }
        }
    }

    const createDocument = (idTableVariant: IdTableVariant) => {
        let lotsId = [] as string[]

        if (!isAllSelect) {
            lotsId = selectedItems.map(c => { return c.idGlobal })
        } else {
            if (selectedItemsInverion.length > 0) 
            {
                lotsId =  selectedItemsInverion.map(c => { return c.idGlobal })
            }
            else {
                lotsId = allIds ?? selectedItems.map(c => { return c.idGlobal })
            }
        }

        const request = {
            idTable: idTableVariant,
            idLotsGlobal: lotsId
        } as ICreateDocumentsRequest 
               

        lotDataProvider.createDocument(request, (response) => {
            setViewState('refresh');
            setShowMessageModal({
                message: `Документ ${response.mnemocode} создан, желаете перейти для дальнейшей работы?`,
                show: true,
                handler: () => {
                    openTabDocument(idTableVariant, response.idGlobal)
                }
            })
        })
    }

    const openTabDocument = (idTableVariant: IdTableVariant, idGlobal: string) => {
        switch (idTableVariant) {
            case IdTableVariant.ActDeduction: {
                return tabCtx.openChild('act_deduction_creator_plugin', "edit", idGlobal)
            }
            case IdTableVariant.Movement: {
                return tabCtx.openChild('movement_creator_plugin', "edit", idGlobal)
            }
            case IdTableVariant.ActRevaluation2: {
                return tabCtx.openChild('act_revaluation_creator_plugin', "edit", idGlobal)
            }
        }
    }

    const handleFiltersLoaded = () => {
        setViewState('refresh');
    };

    useEffect(() => {
        if (!isAllSelect) setSelectedItemsInverion([])
    }, [isAllSelect]);

    const getAnalysisFilterColumns = () => {
        return [
            {
                name: 'likeForLikePercent',
                operator: analysisFilters.salesReport?.CompareSalesCount.value,
                value: analysisFilters?.salesReport?.LflPercentage
            }
        ] as IGridColumnFilter[]
    }

    const getAnalysisSalesReportParamFilter = () => {
        return {
            periodInDays: analysisFilters.salesReport.PeriodInDays,
            IncludeCheques: analysisFilters?.salesReport?.IncludeCheques,
            IncludeMovements: analysisFilters?.salesReport?.IncludeMovements,
            IncludeInvoiceOut: analysisFilters?.salesReport?.IncludeInvoiceOut,
        } as any
    }

    const prepareGridFilter = (): IGridFilter => {
        const columnFilters = getAnalysisFilterColumns().filter(c => c.name !== undefined);
        const paramFilter = getAnalysisSalesReportParamFilter();
        return {
            ...gridFilter,
            columnFilters: [...gridFilter.columnFilters, ...columnFilters],
            paramFilter: { ...gridFilter.paramFilter, ...paramFilter }
        };
    };

    useEffect(() => {
        if (loadDataTimeout) clearTimeout(loadDataTimeout);
        if (viewState == 'refresh') {
            setViewState('view');
            return
        }
        setIsSubmitting(true);

        const currentGridFilter = prepareGridFilter();
        setCorrectGridFilter(currentGridFilter);

        setLoadDataTimeout(setTimeout(() => {
            lotDataProvider.getViewSalesReport(currentGridFilter, (data, totalCount, allIds) => {
                setAllIds(allIds)
                setData(data);
                setTotalCount(totalCount);
                setLoadingStatus(data.length >= 0 ? LoadingStatus.Completed : LoadingStatus.NoData);
                setIsSubmitting(false);
            });
        }, 300))
    }, [viewState, gridFilter, analysisFilters])

    useEffect(() => {
        let found = data.find((d) => d.idGlobal === selectedItem?.idGlobal);
        setSelectedIdGoods(found?.idGoodsGlobal);
        setSelectedIdLot(found?.idGlobal);
    }, [selectedItem])

    useEffect(() => {
        pluginCtx.masterGrid.onUnselectEvent()
    }, [tabCtx.currentTab])

    return <>
        <PluginWrapper>
            <AnalyticalFilters
                setFilters={setAnalysisFilters}
                onFiltersLoaded={handleFiltersLoaded}
                tabId={props.tabId}
            />
            <div className={classNames(styles.separator, styles.separator_horizontal)}></div>
            <ToolbarWrapper withoutMarginBottom={true}>
                <DefaultLotsByCommandsPanel
                    rootTranslation='accounting.lotsByLots.'
                    selectedIdGoods={selectedIdGoods}
                    selectedGoodsName={selectedItem?.goodsName}
                    selectedIdLot={selectedIdLot}
                    selectedItem={[selectedItem, setSelectedItem]}
                    dispatchGridFilter={dispatchGridFilter}
                    dataProvider={lotDataProvider}
                    goodsDataProvider={goodsDataProvider}
                    creatorModalJsx={LotsByLotsCreatorModal}
                    createGoodsModalJsx={GoodsCreatorModal}
                    pluginSettings={props.plugin}
                    setViewState={(vs) => setViewState(vs)}
                    idVariantTable={IdTableVariant.LotsByLots}
                    filters={getFiltersForPrint(pluginSettings, gridFilter)}
                    isHidePanel
                />
            </ToolbarWrapper>
            <FlexRow className={stylesAnalysis.flexRow}>
                <Button className={stylesAnalysis.button} variant={"gray"}
                    onClick={() => {
                        const filter = { ...(correctGridFilter ?? gridFilter), numberPerPage: maxNumberPerPage }
                        const dateNow = DateTime.now().toFormat("yyyy-MM-dd") + "T" + DateTime.now().toFormat("HH:mm:ss");
                        lotDataProvider.getViewAnalysis(filter, (entities) => {
                            exportCsv(entities, dateNow, props.plugin)
                        })
                    }}>
                    {baseT('downloadFileCsv')}
                </Button>
                <Button className={stylesAnalysis.button} variant={"primary"} disabled={isAllowCreateDocument}
                    onClick={() => createDocument(IdTableVariant.ActDeduction)}>
                    {baseT('createActDeduction')}
                </Button>
                <Button className={stylesAnalysis.button} variant={"primary"} disabled={isAllowCreateDocument}
                    onClick={() => createDocument(IdTableVariant.Movement)}>
                    {baseT('createMovement')}
                </Button>
                <Button className={stylesAnalysis.button} variant={"primary"} disabled={isAllowCreateDocument}
                    onClick={() => createDocument(IdTableVariant.ActRevaluation2)}>
                    {baseT('createActRevaluation')}
                </Button>
            </FlexRow>
            <div><Checkbox checked={isAllSelect} label={baseT('selectAll')} onChange={setIsAllSelect} name={""} /></div>

            <div className={styles.pluginWorkspace}>
                <div className={styles.gridWrapper}>
                    <div className={styles.masterGrid}>
                        {
                            isSubmitting ? <Spinner /> : <DefaultGrid
                                gridId={props.gridId}
                                data={data}
                                quantityPosition
                                separator
                                singleDirectory
                                loadingStatus={loadingStatus}
                                setLoadingStatus={setLoadingStatus}
                                totalCount={totalCount}
                                filter={gridFilter}
                                plugin={props.plugin}
                                multipleSelect={multipleSelect}
                                selectedItems={selectedItems}
                                dataProvider={lotDataProvider}
                                getView={(gridFilter, callback) => {
                                    lotDataProvider.getViewSalesReport(gridFilter, (entities,totalCount, allIds) => {
                                        setAllIds(allIds)
                                        callback(entities)
                                        setSelectedItem(undefined);
                                    })
                                }}
                                selectedItem={selectedItem}
                                setPluginSettings={(plugin) => setPluginSettings(plugin)}
                                onSelect={(row) => {
                                    setSelectedItem(row)
                                    row
                                        ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.lots)
                                        : pluginCtx.masterGrid.onUnselectEvent()
                                }}
                                onMultipleSelect={(rows, currentRow) => {
                                    setSelectedItems(rows)
                                    onChangeMultipleSelect(rows, currentRow)
                                }} 
                                onSort={(i) => { dispatchGridFilter({ type: "sort", payload: i.propertyName }) }}
                                onFilterDelete={(i) => { dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName }) }}
                                onPageNumberChange={(n) => { dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } }) }}
                                onNumberPerPageChange={(n) => { dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } }) }}
                                hiddenPagination={{ hiddenNumberPage: true, hiddenCountRow: true, selectAllItems: isAllSelect }}
                            />
                        }
                    </div>
                </div>
            </div>
        </PluginWrapper>
        {showMessageModal.show &&
            <MessageModalWindow
                ok={{
                    onClick: () => {
                        if (showMessageModal.handler) {
                            showMessageModal.handler()
                        }
                        setShowMessageModal({ show: false, message: '' })
                    }
                }}
                cancel={{
                    onClick: () => {
                        setShowMessageModal({ show: false, message: '' })
                    }
                }}
                message={showMessageModal.message}
            />
        }
    </>
}

export default AnalysisSalesLotPluginView